import { useState } from 'react';
import { Check, FileText, FolderPlus, PenLine } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import { ModaleOrientation } from './ModaleOrientation';
import {
  type GrilleOuiNon,
  type LigneEntretien,
  type OptionFiche,
  type OrientationEntretien,
  type ReferentielFiche,
} from '../../lib/useEntretiens';

export type Decision = 'RETENU' | 'NON_RETENU' | 'A_REVOIR';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function jour(valeur: string): string {
  if (!valeur) return '';
  const [date] = valeur.split('T');
  const [annee, mois, jourDuMois] = (date ?? '').split('-');
  return annee && mois && jourDuMois ? `${jourDuMois}/${mois}/${annee}` : '';
}

/** Libellé d'un code coché. Le code brut sert de repli si le référentiel a bougé. */
function libelle(options: OptionFiche[], code: string): string {
  return options.find((option) => option.code === code)?.libelle ?? code;
}

function Rubrique({
  titre,
  children,
}: {
  titre: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="space-y-1.5">
      <h3 className="border-b-2 border-codialis-mint pb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-navy">
        {titre}
      </h3>
      {children}
    </section>
  );
}

/** Une rubrique vide est montrée comme telle : la taire ferait croire qu'elle n'existe pas. */
function Texte({ valeur }: { valeur: string }): JSX.Element {
  return valeur.trim() ? (
    <p className="whitespace-pre-wrap text-sm text-codialis-navy">{valeur}</p>
  ) : (
    <p className="text-sm italic text-codialis-gray">Non renseigné</p>
  );
}

function Coches({ valeurs }: { valeurs: string[] }): JSX.Element {
  if (valeurs.length === 0) return <Texte valeur="" />;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {valeurs.map((valeur) => (
        <li key={valeur}>
          <Pastille texte={valeur} ton="info" />
        </li>
      ))}
    </ul>
  );
}

/** Couple « libellé / valeur », tel que le papier l'aligne. */
function Champ({ libelle: intitule, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
        {intitule}
      </div>
      <Texte valeur={valeur} />
    </div>
  );
}

/**
 * Grille oui/non relue : seules les lignes répondues sont montrées. Une ligne
 * sans réponse n'est pas un « non », et l'afficher comme tel ferait dire au
 * candidat ce qu'il n'a pas dit.
 */
function GrilleRelue({
  options,
  reponses,
}: {
  options: OptionFiche[];
  reponses: GrilleOuiNon;
}): JSX.Element {
  const lignes = options.filter((option) => reponses[option.code]);
  if (lignes.length === 0) return <Texte valeur="" />;
  return (
    <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
      {lignes.map((option) => (
        <li
          key={option.code}
          className="flex items-center justify-between gap-2 bg-white px-3 py-2 text-sm"
        >
          <span className="text-codialis-navy">{option.libelle}</span>
          <Pastille
            texte={reponses[option.code] === 'OUI' ? 'Oui' : 'Non'}
            ton={reponses[option.code] === 'OUI' ? 'success' : 'neutral'}
          />
        </li>
      ))}
    </ul>
  );
}

/** Case oui/non relue : vide tant qu'aucune des deux n'a été cochée. */
function ouiNon(valeur: string): string {
  if (valeur === 'OUI') return 'Oui';
  if (valeur === 'NON') return 'Non';
  return '';
}

/**
 * Compte-rendu d'entretien : ce qui a été dit, le PDF de la fiche de suivi, et
 * la décision.
 *
 * La décision est ici et non dans la fiche : elle se prend après l'entretien,
 * parfois plusieurs jours après, et « retenu » / « non retenu » déplacent le
 * dossier du candidat — c'est un geste à part, pas un champ de formulaire.
 * Ces deux issues ne concernent qu'un candidat : pour une entreprise, l'API
 * les refuse, l'écran ne les propose donc pas.
 */
export function ModaleCompteRendu({
  entretien,
  referentiel,
  enCours,
  erreur,
  onDecision,
  onModifierFiche,
  onVerser,
  versementEnCours,
  versementFait,
  onOuvrirPdf,
  onFermer,
}: {
  entretien: LigneEntretien;
  referentiel: ReferentielFiche;
  enCours: boolean;
  erreur: string | null;
  /** `orientation` accompagne uniquement « non retenu ». */
  onDecision: (decision: Decision, orientation?: OrientationEntretien) => void;
  onModifierFiche: () => void;
  /** Verse le compte-rendu au dossier apprenant, comme pièce jointe. */
  onVerser: () => void;
  versementEnCours: boolean;
  versementFait: boolean;
  onOuvrirPdf: () => void;
  onFermer: () => void;
}): JSX.Element {
  const fiche = entretien.fiche;
  const estCandidat = entretien.type === 'CANDIDAT';
  // « Non retenu » ne s'enregistre pas d'un clic : il demande d'abord vers
  // quelle(s) structure(s) orienter le candidat.
  const [orientationOuverte, setOrientationOuverte] = useState(false);

  const avecAutre = (valeurs: string[], options: OptionFiche[], autre: string): string[] => [
    ...valeurs.map((code) => libelle(options, code)),
    ...(autre.trim() ? [`Autre : ${autre.trim()}`] : []),
  ];

  return (
    <Modale
      titre="Compte-rendu d’entretien"
      sousTitre={`${entretien.interlocuteur} · ${horodatage(entretien.dateEntretien)}`}
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-5 px-4 sm:px-6 py-5">
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={entretien.etatLibelle} ton={entretien.etatTon} />
          <span className="text-xs text-codialis-gray">
            {entretien.objet ?? 'Sans objet précisé'} · {entretien.charge ?? 'Chargé(e) non désigné(e)'}
          </span>
        </div>

        {!fiche ? (
          <div className="rounded-xl bg-codialis-sand px-4 py-3 text-sm text-codialis-gray">
            Aucune fiche d’entretien n’a été remplie pour cet entretien.
          </div>
        ) : (
          <>
            <Rubrique titre="Entretien">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ
                  libelle="Orienté par"
                  valeur={[
                    libelle(referentiel.orienteurs, fiche.orientePar),
                    fiche.orienteParPrecision,
                  ]
                    .filter((partie) => partie !== '')
                    .join(' · ')}
                />
                <Champ libelle="Formation demandée" valeur={fiche.formationDemandee} />
              </div>
            </Rubrique>

            <Rubrique titre="Informations personnelles">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ
                  libelle="Nom et prénoms"
                  valeur={[fiche.identite.nomNaissance, fiche.identite.prenoms]
                    .filter(Boolean)
                    .join(' ')}
                />
                <Champ libelle="Date de naissance" valeur={jour(fiche.identite.dateNaissance)} />
                <Champ libelle="Téléphone" valeur={fiche.identite.telephone} />
                <Champ libelle="Mail" valeur={fiche.identite.mail} />
                <Champ
                  libelle="Adresse"
                  valeur={[fiche.identite.adresse, fiche.identite.codePostal, fiche.identite.ville]
                    .filter(Boolean)
                    .join(' ')}
                />
                <Champ
                  libelle="Logement"
                  valeur={
                    [
                      libelle(referentiel.logements, fiche.logement.statut),
                      fiche.logement.autre,
                    ]
                      .filter(Boolean)
                      .join(' · ') || ''
                  }
                />
                <Champ
                  libelle="Situation familiale"
                  valeur={
                    fiche.familiale.statut
                      ? libelle(referentiel.situationsFamiliales, fiche.familiale.statut)
                      : ''
                  }
                />
                <Champ libelle="Suivi de justice" valeur={ouiNon(fiche.familiale.suiviJustice)} />
              </div>
              <Champ
                libelle="Personne à contacter en cas d’urgence"
                valeur={fiche.familiale.contactUrgence}
              />
              {fiche.familiale.responsableLegal && (
                <Champ libelle="Responsable légal" valeur={fiche.familiale.responsableLegal} />
              )}
            </Rubrique>

            <Rubrique titre="Situation actuelle">
              <Coches
                valeurs={avecAutre(
                  fiche.situation.statuts,
                  referentiel.situationsActuelles,
                  fiche.situation.autre,
                )}
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ libelle="Reconnaissance RQTH" valeur={ouiNon(fiche.situation.rqth)} />
                {fiche.situation.rqth === 'NON' && (
                  <Champ
                    libelle="Aide au dossier RQTH"
                    valeur={[
                      ouiNon(fiche.situation.aideDossierRqth),
                      fiche.situation.aideDossierRqthCommentaire,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ
                  libelle="Permis de conduire"
                  valeur={[
                    fiche.mobilite.permis
                      ? libelle(referentiel.etatsPermis, fiche.mobilite.permis)
                      : '',
                    fiche.mobilite.permisPrecision,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Champ libelle="Zone de déplacement" valeur={fiche.mobilite.zoneDeplacement} />
              </div>
              <Coches
                valeurs={avecAutre(
                  fiche.mobilite.locomotion,
                  referentiel.moyensLocomotion,
                  fiche.mobilite.locomotionAutre,
                )}
              />
            </Rubrique>

            <Rubrique titre="Ressources">
              <Coches
                valeurs={avecAutre(
                  fiche.ressources.allocationsCaf,
                  referentiel.allocationsCaf,
                  fiche.ressources.allocationAutre,
                )}
              />
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ
                  libelle="Inscription à France Travail"
                  valeur={[
                    ouiNon(fiche.ressources.franceTravail),
                    ...fiche.ressources.aidesFranceTravail.map((code) =>
                      libelle(referentiel.aidesFranceTravail, code),
                    ),
                    fiche.ressources.aideAutre,
                    ...(fiche.ressources.franceTravail === 'OUI'
                      ? [
                          fiche.ressources.conseillerFranceTravail &&
                            `Conseiller(ère) : ${fiche.ressources.conseillerFranceTravail}`,
                          fiche.ressources.identifiantFranceTravail &&
                            `Identifiant : ${fiche.ressources.identifiantFranceTravail}`,
                        ]
                      : []),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Champ
                  libelle="Inscription Mission Locale"
                  valeur={[
                    ouiNon(fiche.ressources.missionLocale),
                    fiche.ressources.conseillerMissionLocale,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Champ libelle="AAH" valeur={ouiNon(fiche.ressources.aah)} />
                <Champ
                  libelle="Couverture sociale"
                  valeur={fiche.ressources.couvertureSociale
                    .map((code) => libelle(referentiel.couverturesSociales, code))
                    .join(', ')}
                />
              </div>
            </Rubrique>

            <Rubrique titre="Parcours et expériences">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ libelle="Niveau de formation" valeur={fiche.parcours.niveauFormation} />
                <Champ libelle="Dernier diplôme obtenu" valeur={fiche.parcours.dernierDiplome} />
                <Champ
                  libelle="Inscrit dans un CFA"
                  valeur={[ouiNon(fiche.parcours.inscritCfa), fiche.parcours.cfaLequel]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Champ libelle="Dernier emploi occupé" valeur={fiche.experiences.dernierEmploi} />
              </div>
              <Champ
                libelle="Dernière formation ou titre professionnel"
                valeur={fiche.parcours.derniereFormation}
              />
              {fiche.experiences.dejaTravailleDomaine === 'OUI' && (
                <Champ
                  libelle="Expérience dans le domaine"
                  valeur={[
                    fiche.experiences.entreprise,
                    fiche.experiences.fonction,
                    fiche.experiences.duree,
                    fiche.experiences.missions,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              )}
            </Rubrique>

            <Rubrique titre="Demande de formation et projet">
              <Champ libelle="Poste recherché" valeur={fiche.projet.posteRecherche} />
              <Champ libelle="Motivation" valeur={fiche.projet.motivation} />
              <Coches
                valeurs={avecAutre(
                  fiche.projet.objectifs,
                  referentiel.objectifsFormation,
                  fiche.projet.objectifAutre,
                )}
              />
            </Rubrique>

            <Rubrique titre="Compétences et aptitudes">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ libelle="CV à jour" valeur={ouiNon(fiche.competences.cvAJour)} />
                <Champ
                  libelle="Parle de ses points forts / faibles"
                  valeur={[
                    ouiNon(fiche.competences.pointsForts),
                    fiche.competences.pointsFortsPrecision,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              </div>
              <GrilleRelue
                options={referentiel.savoirsBase}
                reponses={fiche.competences.savoirs}
              />
              <GrilleRelue
                options={referentiel.outilsNumeriques}
                reponses={fiche.competences.numeriques}
              />
              {fiche.competences.commentaires && (
                <Champ libelle="Commentaires" valeur={fiche.competences.commentaires} />
              )}
            </Rubrique>

            <Rubrique titre="Situation de handicap">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ
                  libelle="Situation de handicap ou besoin d’aménagement"
                  valeur={ouiNon(fiche.handicap.situation)}
                />
                <Champ
                  libelle="Souhaite rencontrer le référent handicap"
                  valeur={ouiNon(fiche.handicap.rencontrerReferent)}
                />
              </div>
              {fiche.handicap.besoins && (
                <Champ libelle="Besoins exprimés" valeur={fiche.handicap.besoins} />
              )}
            </Rubrique>

            <Rubrique titre="Disponibilités et contraintes">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Champ
                  libelle="Disponibilité"
                  valeur={
                    fiche.disponibilites.disponibilite
                      ? libelle(referentiel.disponibilites, fiche.disponibilites.disponibilite)
                      : ''
                  }
                />
                <Champ
                  libelle="Disponible sur toute la formation"
                  valeur={ouiNon(fiche.disponibilites.dureeFormation)}
                />
                <Champ
                  libelle="Enquête métier ou PMSMP"
                  valeur={[
                    ouiNon(fiche.disponibilites.enqueteMetier),
                    fiche.disponibilites.enquetePrecision,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
                <Champ
                  libelle="Contraintes particulières"
                  valeur={fiche.disponibilites.contraintesParticulieres}
                />
              </div>
              <GrilleRelue
                options={referentiel.conditionsTravail}
                reponses={fiche.disponibilites.conditionsTravail}
              />
              <GrilleRelue
                options={referentiel.contraintesImpact}
                reponses={fiche.disponibilites.contraintesImpact}
              />
              {fiche.disponibilites.precisionsContraintes && (
                <Champ
                  libelle="Précisions"
                  valeur={fiche.disponibilites.precisionsContraintes}
                />
              )}
              {fiche.disponibilites.autresRemarques && (
                <Champ
                  libelle="Autres remarques"
                  valeur={fiche.disponibilites.autresRemarques}
                />
              )}
            </Rubrique>

            <Rubrique titre="Conclusion de l’entretien">
              <Texte valeur={fiche.conclusion} />
            </Rubrique>
          </>
        )}

        {entretien.orientation && (
          <Rubrique titre="Orientation proposée">
            {entretien.orientation.structures.length === 0 ? (
              <p className="text-sm italic text-codialis-gray">Aucune structure proposée</p>
            ) : (
              <Coches
                valeurs={entretien.orientation.structures.map((code) =>
                  code === 'AUTRE' && entretien.orientation?.autre
                    ? `Autre : ${entretien.orientation.autre}`
                    : libelle(referentiel.orientations, code),
                )}
              />
            )}
          </Rubrique>
        )}

        {entretien.besoinsRecueillis && (
          <Rubrique titre="Besoins recueillis">
            <Texte valeur={entretien.besoinsRecueillis} />
          </Rubrique>
        )}

        {entretien.compteRendu && (
          <Rubrique titre="Observations du chargé de recrutement">
            <Texte valeur={entretien.compteRendu} />
          </Rubrique>
        )}

      </div>

      <div className="sticky bottom-0 space-y-3 rounded-b-2xl border-t border-gray-100 bg-white px-4 sm:px-6 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onOuvrirPdf}
            className="inline-flex items-center gap-1.5 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Ouvrir le compte-rendu PDF
          </button>
          <BoutonSecondaire onClick={onModifierFiche} disabled={enCours}>
            <span className="inline-flex items-center gap-1.5">
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Modifier les réponses
            </span>
          </BoutonSecondaire>
          {/* Le versement n'a de sens que pour un candidat : un entretien
              d'entreprise n'a pas de dossier apprenant où déposer la pièce.
              L'API le refuse de toute façon, l'écran ne le propose pas. */}
          {entretien.apprenantId && (
            <BoutonSecondaire onClick={onVerser} disabled={versementEnCours}>
              <span className="inline-flex items-center gap-1.5">
                <FolderPlus className="h-4 w-4" aria-hidden="true" />
                {versementEnCours ? 'Versement…' : 'Verser au dossier'}
              </span>
            </BoutonSecondaire>
          )}
        </div>

        {entretien.apprenantId && (
          <p className="text-[11px] text-codialis-gray">
            {versementFait
              ? 'Compte-rendu versé au dossier apprenant, section « Entretien ».'
              : 'Le versement dépose un exemplaire figé dans le dossier apprenant. Une fiche modifiée ensuite ne le réécrit pas : elle donne lieu à un nouveau versement.'}
          </p>
        )}

        {/* L'erreur est ici, contre les boutons : au-dessus du compte-rendu,
            elle restait hors de l'écran au moment du clic. */}
        {erreur && <MessageErreur message={erreur} />}

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <span className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
            Décision
          </span>
          {estCandidat && (
            <>
              <BoutonDecision
                libelle="Retenu"
                actif={entretien.etat === 'RETENU'}
                ton="mint"
                enCours={enCours}
                onClick={() => onDecision('RETENU')}
              />
              <BoutonDecision
                libelle="Non retenu"
                actif={entretien.etat === 'NON_RETENU'}
                ton="danger"
                enCours={enCours}
                onClick={() => setOrientationOuverte(true)}
              />
            </>
          )}
          <BoutonDecision
            libelle="À revoir"
            actif={entretien.etat === 'A_REVOIR'}
            ton="neutre"
            enCours={enCours}
            onClick={() => onDecision('A_REVOIR')}
          />
        </div>

        {estCandidat && (
          <p className="text-[11px] text-codialis-gray">
            Une issue « retenu » saisie ici ne remplace pas l’envoi du lien d’accès : il se
            déclenche depuis l’écran des inscriptions, avec la liste des pièces.
          </p>
        )}
      </div>

      {orientationOuverte && (
        <ModaleOrientation
          interlocuteur={entretien.interlocuteur}
          options={referentiel.orientations}
          initiale={entretien.orientation}
          enCours={enCours}
          erreur={erreur}
          onConfirmer={(orientation) => onDecision('NON_RETENU', orientation)}
          onFermer={() => setOrientationOuverte(false)}
        />
      )}
    </Modale>
  );
}

const TONS_DECISION: Record<string, string> = {
  mint: 'border-codialis-mint bg-codialis-mint/15 text-codialis-navy hover:bg-codialis-mint/25',
  danger: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
  neutre: 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50',
};

function BoutonDecision({
  libelle: texte,
  actif,
  ton,
  enCours,
  onClick,
}: {
  libelle: string;
  actif: boolean;
  ton: 'mint' | 'danger' | 'neutre';
  enCours: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={enCours}
      aria-pressed={actif}
      className={`inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
        TONS_DECISION[ton]
      } ${actif ? 'ring-2 ring-codialis-navy/30' : ''}`}
    >
      {actif && <Check className="h-4 w-4" aria-hidden="true" />}
      {texte}
    </button>
  );
}
